import React, { useState, useEffect, useRef } from "react";
import {
  Button, Card, CardBody, CardHeader, Container, Modal, ModalBody,
  ModalFooter, ModalHeader, Form, FormGroup, Input, Label, Row, Col,
  Spinner, Badge, Nav, NavItem, NavLink, Alert, Dropdown, DropdownToggle, DropdownMenu, DropdownItem
} from "reactstrap";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Toast } from "primereact/toast";
import { useParams } from "react-router-dom";
import { MoreVertical, Plus, Trash, Edit, Eye } from "lucide-react";

const StaffPortfolio = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [staffName, setStaffName] = useState("");
  const [activeTab, setActiveTab] = useState("portfolio");
  const [portfolioExists, setPortfolioExists] = useState(null);
  const toast = useRef(null);

  // Forms state
  const [portfolioForm, setPortfolioForm] = useState({
    title: "",
    description: "",
    image: null,
    url: "",
    bannerheading1: "",
    bannerheading2: "",
    socials: {
      instagram: "",
      behance: "",
      linkedin: "",
      twitter: "",
      facebook: ""
    }
  });

  const [workForm, setWorkForm] = useState({
    id: null,
    name: "",
    image: null,
    url: ""
  });

  const [skillForm, setSkillForm] = useState({
    id: null,
    name: "",
    proficiency: ""
  });

  // Modals state
  const [addWorkModal, setAddWorkModal] = useState(false);
  const [addSkillModal, setAddSkillModal] = useState(false);
  const [deleteWorkModal, setDeleteWorkModal] = useState(false);
  const [deleteSkillModal, setDeleteSkillModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const toggleDropdown = (id) => {
    if (dropdownOpen === id) {
      setDropdownOpen(null);
    } else {
      setDropdownOpen(id);
    }
  };

  const toggleWorkModal = () => {
    setAddWorkModal(!addWorkModal);
    if (!addWorkModal) {
      setWorkForm({ id: null, name: "", image: null, url: "" });
    }
  };

  const toggleSkillModal = () => {
    setAddSkillModal(!addSkillModal);
    if (!addSkillModal) {
      setSkillForm({ id: null, name: "", proficiency: "" });
    }
  };

  const toggleDeleteWorkModal = () => setDeleteWorkModal(!deleteWorkModal);
  const toggleDeleteSkillModal = () => setDeleteSkillModal(!deleteSkillModal);

  const breadcrumbItems = [
    { title: "HR", link: "/" },
    { title: "Staffs", link: "/admin/list-staff" },
    { title: "Portfolio", link: "#!" },
  ];

  // Fetch portfolio data
  const fetchPortfolio = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/portfolio/get-user-portfolio/${id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      if (response.data?.status === "success" && response.data.data) {
        const portfolioData = response.data.data;
        console.log("Received portfolio data:", portfolioData);

        // Handle socials data
        let socials = {};
        if (portfolioData.socials) {
          try {
            socials = typeof portfolioData.socials === 'string'
              ? JSON.parse(portfolioData.socials)
              : portfolioData.socials;
          } catch (e) {
            console.error("Error parsing socials:", e);
          }
        }

        // Set default empty strings for all social fields
        const defaultSocials = {
          instagram: "",
          behance: "",
          linkedin: "",
          twitter: "",
          facebook: ""
        };

        setPortfolio(portfolioData);
        setPortfolioExists(true);
        setPortfolioForm({
          ...portfolioForm,
          title: portfolioData.title || "",
          description: portfolioData.description || "",
          url: portfolioData.url || "",
          bannerheading1: portfolioData.bannerheading1 || "",
          bannerheading2: portfolioData.bannerheading2 || "",
          socials: { ...defaultSocials, ...socials }
        });
      } else {
        setPortfolioExists(false);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setPortfolioExists(false);
    } finally {
      setLoading(false);
    }
  }, [id, portfolioForm]);

  const fetchStaffInfo = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/staffs/fetch/${id}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      if (response.data && response.data.status === "success") {
        setStaffName(response.data.data.name || "Staff");
      }
    } catch (error) {
      console.error("Error fetching staff info:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchPortfolio();
    fetchStaffInfo();
  }, [fetchPortfolio, fetchStaffInfo]);

  const handleSubmitPortfolio = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("user_id", id);
    formData.append("title", portfolioForm.title);
    formData.append("description", portfolioForm.description);

    if (portfolioForm.image) {
      formData.append("image", portfolioForm.image);
    }

    formData.append("url", portfolioForm.url);
    formData.append("bannerheading1", portfolioForm.bannerheading1);
    formData.append("bannerheading2", portfolioForm.bannerheading2);

    // Create a clean socials object with non-empty values
    formData.append("instagram", portfolioForm.socials.instagram || "");
    formData.append("behance", portfolioForm.socials.behance || "");
    formData.append("linkedin", portfolioForm.socials.linkedin || "");
    formData.append("twitter", portfolioForm.socials.twitter || "");
    formData.append("facebook", portfolioForm.socials.facebook || "");

    try {
      const endpoint = portfolio
        ? `${API_BASE_URL}/portfolio/update/${portfolio.id}`
        : `${API_BASE_URL}/portfolio/add`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status === "success") {
        toast.current.show({
          severity: "success",
          summary: portfolio ? "Updated" : "Created",
          detail: `Portfolio ${portfolio ? "updated" : "created"} successfully`,
          life: 3000
        });
        fetchPortfolio();
      }
    } catch (error) {
      console.error("Error saving portfolio:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to save portfolio",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitWork = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("portfolio_id", portfolio.id);
    formData.append("name", workForm.name);
    if (workForm.image) {
      formData.append("image", workForm.image);
    }
    formData.append("url", workForm.url);

    try {
      const endpoint = workForm.id
        ? `${API_BASE_URL}/portfolio/portfolio-work/update/${workForm.id}`
        : `${API_BASE_URL}/portfolio/portfolio-work/add`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data && response.data.status === "success") {
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: workForm.id ? "Updated" : "Added",
            detail: `Work ${workForm.id ? "updated" : "added"} successfully`,
            life: 3000
          });
        }
        toggleWorkModal();
        fetchPortfolio();
      }
    } catch (error) {
      console.error("Error saving work:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to ${workForm.id ? "update" : "add"} work`,
          life: 3000
        });
      }
    }
    setLoading(false);
  };

  const handleSubmitSkill = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("portfolio_id", portfolio.id);
    formData.append("name", skillForm.name);
    formData.append("proficiency", skillForm.proficiency);

    try {
      const endpoint = skillForm.id
        ? `${API_BASE_URL}/portfolio/portfolio-skill/update/${skillForm.id}`
        : `${API_BASE_URL}/portfolio/portfolio-skill/add`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data && response.data.status === "success") {
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: skillForm.id ? "Updated" : "Added",
            detail: `Skill ${skillForm.id ? "updated" : "added"} successfully`,
            life: 3000
          });
        }
        toggleSkillModal();
        fetchPortfolio();
      }
    } catch (error) {
      console.error("Error saving skill:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: `Failed to ${skillForm.id ? "update" : "add"} skill`,
          life: 3000
        });
      }
    }
    setLoading(false);
  };

  const deleteWork = async () => {
    if (!itemToDelete) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/portfolio/portfolio-work/delete/${itemToDelete}`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );

      if (response.data && response.data.status === "success") {
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Deleted",
            detail: "Work deleted successfully",
            life: 3000
          });
        }
        fetchPortfolio();
      }
    } catch (error) {
      console.error("Error deleting work:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete work",
          life: 3000
        });
      }
    }

    setItemToDelete(null);
    toggleDeleteWorkModal();
  };

  const deleteSkill = async () => {
    if (!itemToDelete) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/portfolio/portfolio-skill/delete/${itemToDelete}`,
        {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );

      if (response.data && response.data.status === "success") {
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Deleted",
            detail: "Skill deleted successfully",
            life: 3000
          });
        }
        fetchPortfolio();
      }
    } catch (error) {
      console.error("Error deleting skill:", error);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete skill",
          life: 3000
        });
      }
    }

    setItemToDelete(null);
    toggleDeleteSkillModal();
  };

  const renderPortfolioForm = () => (
    <Card>
      <CardHeader>
        <h4 className="card-title mb-0">
          {portfolio ? "Edit Portfolio" : "Create Portfolio"} for {staffName}
        </h4>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleSubmitPortfolio}>
          <Row>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label>Title</Label>
                <Input
                  type="text"
                  value={portfolioForm.title}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, title: e.target.value })}
                  required
                />
              </FormGroup>
            </Col>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label>URL</Label>
                <Input
                  type="url"
                  value={portfolioForm.url}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, url: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label>Banner Heading 1</Label>
                <Input
                  type="text"
                  value={portfolioForm.bannerheading1}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, bannerheading1: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={6} className="mb-3">
              <FormGroup>
                <Label>Banner Heading 2</Label>
                <Input
                  type="text"
                  value={portfolioForm.bannerheading2}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, bannerheading2: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={12} className="mb-3">
              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  rows="4"
                  value={portfolioForm.description}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })}
                  required
                />
              </FormGroup>
            </Col>
            <Col md={4} className="mb-3">
              <FormGroup>
                <Label>Portfolio Image</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPortfolioForm({ ...portfolioForm, image: file });
                    }
                  }}
                  required={!portfolio}
                />
                <small className="text-muted">
                  {portfolio ? "Upload new image to replace current one" : "Upload profile image for the portfolio"}
                </small>
              </FormGroup>
            </Col>
            {portfolio && portfolio.image && (
              <Col md={2} className="mb-3">
                <Label>Current Image</Label>
                <img
                  src={portfolio.image}
                  alt="Portfolio Cover"
                  className="img-thumbnail"
                  style={{ maxHeight: "100px" }}
                />
              </Col>
            )}

            <Col md={12} className="mb-3">
              <h5 className="mb-3">Social Media Links</h5>
              <Row>
                <Col md={4} className="mb-3">
                  <FormGroup>
                    <Label>Instagram</Label>
                    <Input
                      type="url"
                      value={portfolioForm.socials.instagram || ''}
                      onChange={(e) => setPortfolioForm({
                        ...portfolioForm,
                        socials: { ...portfolioForm.socials, instagram: e.target.value }
                      })}
                      placeholder="https://instagram.com/username"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="mb-3">
                  <FormGroup>
                    <Label>Behance</Label>
                    <Input
                      type="url"
                      value={portfolioForm.socials.behance || ''}
                      onChange={(e) => setPortfolioForm({
                        ...portfolioForm,
                        socials: { ...portfolioForm.socials, behance: e.target.value }
                      })}
                      placeholder="https://behance.net/username"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="mb-3">
                  <FormGroup>
                    <Label>LinkedIn</Label>
                    <Input
                      type="url"
                      value={portfolioForm.socials.linkedin || ''}
                      onChange={(e) => setPortfolioForm({
                        ...portfolioForm,
                        socials: { ...portfolioForm.socials, linkedin: e.target.value }
                      })}
                      placeholder="https://linkedin.com/in/username"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="mb-3">
                  <FormGroup>
                    <Label>Twitter</Label>
                    <Input
                      type="url"
                      value={portfolioForm.socials.twitter || ''}
                      onChange={(e) => setPortfolioForm({
                        ...portfolioForm,
                        socials: { ...portfolioForm.socials, twitter: e.target.value }
                      })}
                      placeholder="https://twitter.com/username"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} className="mb-3">
                  <FormGroup>
                    <Label>Facebook</Label>
                    <Input
                      type="url"
                      value={portfolioForm.socials.facebook || ''}
                      onChange={(e) => setPortfolioForm({
                        ...portfolioForm,
                        socials: { ...portfolioForm.socials, facebook: e.target.value }
                      })}
                      placeholder="https://facebook.com/username"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </Row>

          <div className="text-center">
            <Button color="primary" type="submit" disabled={loading}>
              {portfolio ? "Update Portfolio" : "Create Portfolio"}
              {loading && <Spinner size="sm" className="ms-2" />}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );

  if (loading && portfolioExists === null) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner size="lg" />
            <p className="mt-3">Loading portfolio data...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Portfolio" breadcrumbItems={breadcrumbItems} />

          {portfolioExists === false && (
            <Alert color="info" className="mb-4">
              <h4 className="alert-heading">No Portfolio Found</h4>
              <p className="mb-0">This person doesn't have a portfolio yet. Use the form below to create one.</p>
            </Alert>
          )}

          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={activeTab === "portfolio" ? "active" : ""}
                onClick={() => setActiveTab("portfolio")}
                style={{ cursor: "pointer" }}
              >
                Portfolio
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "works" ? "active" : ""}
                onClick={() => setActiveTab("works")}
                style={{ cursor: "pointer" }}
                disabled={!portfolioExists}
              >
                Works
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "skills" ? "active" : ""}
                onClick={() => setActiveTab("skills")}
                style={{ cursor: "pointer" }}
                disabled={!portfolioExists}
              >
                Skills
              </NavLink>
            </NavItem>
          </Nav>

          {activeTab === "portfolio" && renderPortfolioForm()}

          {activeTab === "works" && portfolioExists && (
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">Portfolio Works</h4>
                <Button color="primary" onClick={toggleWorkModal}>
                  <Plus size={14} className="me-1" /> Add New Work
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : portfolio && portfolio.works && portfolio.works.length > 0 ? (
                  <Row>
                    {portfolio.works.map((work, index) => (
                      <Col md={4} key={index} className="mb-4">
                        <Card className="h-100">
                          {work.image && (
                            <div
                              className="card-img-top"
                              style={{
                                height: '160px',
                                backgroundImage: `url(${work.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            />
                          )}
                          <CardBody>
                            <div className="d-flex justify-content-between">
                              <h5>{work.name}</h5>
                              <div className="mt d-flex ">
                                <Dropdown isOpen={dropdownOpen === work.id} toggle={() => toggleDropdown(work.id)}>
                                  <DropdownToggle tag="button" className="btn btn-light">
                                    <MoreVertical size={20} />
                                  </DropdownToggle>
                                  <DropdownMenu>
                                    {work.url && (
                                      <DropdownItem
                                        tag="a"
                                        href={work.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <Eye size={16} className="me-2" />
                                        View
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={() => {
                                        setWorkForm({
                                          id: work.id,
                                          name: work.name,
                                          image: null,
                                          url: work.url
                                        });
                                        setAddWorkModal(true);
                                      }}
                                    >
                                      <Edit size={16} className="me-2" />
                                      Edit
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={() => {
                                        setItemToDelete(work.id);
                                        toggleDeleteWorkModal();
                                      }}
                                      className="text-danger"
                                    >
                                      <Trash size={16} className="me-2" />
                                      Delete
                                    </DropdownItem>
                                  </DropdownMenu>
                                </Dropdown>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center py-5">
                    <p>No works added yet. Click "Add New Work" to get started.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {activeTab === "skills" && portfolioExists && (
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">Skills</h4>
                <Button color="primary" onClick={toggleSkillModal}>
                  <Plus size={14} className="me-1" /> Add New Skill
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : portfolio && portfolio.skills && portfolio.skills.length > 0 ? (
                  <Row>
                    {portfolio.skills.map((skill, index) => {
                      // Determine badge color based on proficiency
                      let badgeColor = "primary";
                      if (skill.proficiency >= 80) badgeColor = "success";
                      else if (skill.proficiency >= 50) badgeColor = "info";
                      else if (skill.proficiency >= 30) badgeColor = "warning";
                      else badgeColor = "danger";

                      // Determine dropdown toggle color
                      let dropdownColor = "light";
                      if (skill.proficiency >= 80) dropdownColor = "outline-success";
                      else if (skill.proficiency >= 50) dropdownColor = "outline-info";
                      else if (skill.proficiency >= 30) dropdownColor = "outline-warning";
                      else dropdownColor = "outline-danger";

                      return (
                        <Col md={6} key={index} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h5 className="mb-0">{skill.name}</h5>
                            <div className="d-flex align-items-center">
                              <Badge color={badgeColor} className="me-2">
                                {skill.proficiency}%
                              </Badge>
                              <Dropdown isOpen={dropdownOpen === skill.id} toggle={() => toggleDropdown(skill.id)}>
                                <DropdownToggle
                                  tag="button"
                                  className={`btn btn-${dropdownColor}`}
                                  style={{ padding: '0.25rem 0.5rem' }}
                                >
                                  <MoreVertical size={16} />
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem
                                    onClick={() => {
                                      setSkillForm({
                                        id: skill.id,
                                        name: skill.name,
                                        proficiency: skill.proficiency
                                      });
                                      setAddSkillModal(true);
                                    }}
                                  >
                                    <Edit size={16} className="me-2" />
                                    Edit
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => {
                                      setItemToDelete(skill.id);
                                      toggleDeleteSkillModal();
                                    }}
                                    className="text-danger"
                                  >
                                    <Trash size={16} className="me-2" />
                                    Delete
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                          </div>
                          <div className="progress" style={{ height: "10px" }}>
                            <div
                              className={`progress-bar bg-${badgeColor}`}
                              role="progressbar"
                              style={{ width: `${skill.proficiency}%` }}
                              aria-valuenow={skill.proficiency}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div className="text-center py-5">
                    <p>No skills added yet. Click "Add New Skill" to get started.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </Container>
      </div>

      {/* Add/Edit Work Modal */}
      <Modal isOpen={addWorkModal} toggle={toggleWorkModal}>
        <Form onSubmit={handleSubmitWork}>
          <ModalHeader toggle={toggleWorkModal}>
            {workForm.id ? "Edit Work" : "Add New Work"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                value={workForm.name}
                onChange={(e) => setWorkForm({ ...workForm, name: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>URL</Label>
              <Input
                type="url"
                value={workForm.url}
                onChange={(e) => setWorkForm({ ...workForm, url: e.target.value })}
                placeholder="https://example.com/work"
              />
            </FormGroup>
            <FormGroup>
              <Label>Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setWorkForm({ ...workForm, image: file });
                  }
                }}
                required={!workForm.id}
              />
              {workForm.id && (
                <small className="text-muted">Leave empty to keep current image</small>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleWorkModal}>Cancel</Button>
            <Button color="primary" type="submit" disabled={loading}>
              {workForm.id ? "Update" : "Add"} Work
              {loading && <Spinner size="sm" className="ms-2" />}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Add/Edit Skill Modal */}
      <Modal isOpen={addSkillModal} toggle={toggleSkillModal}>
        <Form onSubmit={handleSubmitSkill}>
          <ModalHeader toggle={toggleSkillModal}>
            {skillForm.id ? "Edit Skill" : "Add New Skill"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Skill Name</Label>
              <Input
                type="text"
                value={skillForm.name}
                onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })}
                required
                placeholder="e.g., Graphic Design, JavaScript"
              />
            </FormGroup>
            <FormGroup>
              <Label>Proficiency</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={skillForm.proficiency}
                onChange={(e) => setSkillForm({ ...skillForm, proficiency: e.target.value })}
                required
                placeholder="Enter a value between 1 and 100"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleSkillModal}>Cancel</Button>
            <Button color="primary" type="submit" disabled={loading}>
              {skillForm.id ? "Update" : "Add"} Skill
              {loading && <Spinner size="sm" className="ms-2" />}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Work Confirmation Modal */}
      <Modal isOpen={deleteWorkModal} toggle={toggleDeleteWorkModal}>
        <ModalHeader toggle={toggleDeleteWorkModal}>Delete Work</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this work? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteWorkModal}>Cancel</Button>
          <Button color="danger" onClick={deleteWork}>Delete</Button>
        </ModalFooter>
      </Modal>

      {/* Delete Skill Confirmation Modal */}
      <Modal isOpen={deleteSkillModal} toggle={toggleDeleteSkillModal}>
        <ModalHeader toggle={toggleDeleteSkillModal}>Delete Skill</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this skill? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteSkillModal}>Cancel</Button>
          <Button color="danger" onClick={deleteSkill}>Delete</Button>
        </ModalFooter>
      </Modal>

      <Toast ref={toast} />
    </React.Fragment>
  );
};

export default StaffPortfolio;